import * as React from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'

import { CloseOutlined } from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import { OVERLAY_PANEL_CLASS } from './overlay-panel'

/* -------------------------------------------------------------------------
 * shadcn `Dialog` primitive. Backs the `src/antd/Modal` seam's shadcn half;
 * the antd-compatible props are mapped there, this file is the Radix wrapper.
 *
 * Ported from the `update-design` reference branch, with three changes that
 * matter on THIS branch:
 *
 * 1. THE CLOSE GLYPH comes from `src/icons/antd-compat`, not `lucide-react`.
 *    `components.json` is the reference branch's copy and says
 *    `iconLibrary: lucide`, but outside the icon seam a raw lucide import
 *    renders lucide with the design toggle OFF — AGENTS.md forbids it and
 *    `src/antd/__tests__/seam-adoption.evidence.test.tsx` fails on it.
 * 2. THE PANEL SCROLLS. antd's Modal sits 100px from the top of a scrolling
 *    page, so a tall modal is always reachable. A `fixed` + `translate`
 *    centred panel is not: anything taller than the viewport is simply cut
 *    off. `max-h` + `overflow-y-auto` is what keeps a long form usable.
 * 3. THE MASK IS OPTIONAL BUT MODALITY IS NOT. `mask={false}` in antd removes
 *    the dim layer and keeps the focus trap; Radix's `modal` prop controls
 *    both at once. So the overlay is what `mask` gates here, and the seam
 *    leaves `modal` alone — see `src/antd/Modal/Modal.shadcn.tsx`.
 * ------------------------------------------------------------------------- */

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

// `forwardRef`, not a bare function: Radix's Portal renders each child through
// a Slot and its `Presence` reads the resulting node to time the exit
// animation. A wrapper that swallows the ref makes the overlay disappear
// instantly instead of fading, and logs "Function components cannot be given
// refs".
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[1000] bg-black/45',
        className
      )}
      {...props}
    />
  )
})

function DialogContent({
  className,
  children,
  showCloseButton = true,
  mask = true,
  maskStyle,
  container,
  closeLabel = 'Close',
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  mask?: boolean
  maskStyle?: React.CSSProperties
  /** Portal target. `undefined` = `document.body`, the default. */
  container?: HTMLElement | null
  closeLabel?: string
}) {
  return (
    <DialogPortal container={container ?? undefined}>
      {mask && <DialogOverlay style={maskStyle} />}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(OVERLAY_PANEL_CLASS, className)}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            aria-label={closeLabel}
            // `px-1.5 py-px` reproduces Chrome's UA `button { padding: 1px
            // 6px }`, which this button used to inherit and which the
            // `[data-slot]` base layer in `src/styles/tailwind.css` now
            // resets away. Without it the box shrinks 12px/2px and the glyph
            // moves — the rendering #982 signed off on stays put with it.
            className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-xs px-1.5 py-px opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0"
          >
            <CloseOutlined aria-hidden />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-left', className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-row justify-end gap-2', className)}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-base leading-6 font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
